import type { Provenance, Bundle, BundleEntry, FhirResource } from 'fhir/r4';
import moment from 'moment';
import { v4 as uuid } from 'uuid';

import * as FHIRUtils from '../fhirutils';

/**
 * SUSPECTED DEFECT (`Organization/undefined` written into Provenance) — REPORTED, NOT
 * FIXED. The parameter default only applies when the argument is OMITTED. Eleven call
 * sites instead pass a constructed object — `{ code: orgCode && orgCode.toUpperCase(),
 * display: orgCode && orgCode.toLowerCase() }` — where `orgCode` derives from
 * `patient.managingOrganization.reference.split('/')[1]`. `reference` is `0..1` in
 * FHIR, so a member with no managing organization yields `undefined` for both fields,
 * the default is bypassed because an argument WAS supplied, and `buildProvenance`
 * writes `reference: 'Organization/' + org.code` — the literal string
 * `"Organization/undefined"` — onto a real Provenance record for a real clinical write.
 *
 * The parameter type states the union the callers actually pass. Substituting the
 * HUMANA default for an absent code here would silently attribute another
 * organization's provenance to the write, which is a worse outcome than the visible
 * bad reference; and coalescing to `''` would produce `"Organization/"`. Which of
 * those a member with no managing organization should get is a clinical call.
 */
export function build(
  targetRef: string,
  actorRef: { reference: string; display: string },
  patientId: string | undefined,
  activity: string,
  reasonDesc: string,
  orgRef: { code: string | undefined; display: string | undefined } = { code: 'HUMANA', display: 'humana' },
  piiSurveyQRID: string | undefined = undefined
) {
  const date = new Date();
  const occured = moment(date).format('YYYY-MM-DD');
  const recorded = date.toISOString();
  const provenance: Provenance = buildProvenance(
    targetRef,
    actorRef,
    orgRef,
    `Patient/${patientId}`,
    occured,
    recorded,
    activity,
    activity.toLowerCase(),
    'LG41764-8',
    reasonDesc
  );
  if (piiSurveyQRID) {
    provenance.extension = [
      {
        url: 'https://projectwell.io/fhir/questionnaire-response',
        valueString: piiSurveyQRID,
      },
    ];
  }
  return provenance;
}

export function buildProvenanceBundle(fhirResources: Array<FhirResource>, provenancce: Provenance) {
  const fhirBundle: Bundle = FHIRUtils.buildEmptyBundle('transaction');
  const bundleEntries: BundleEntry[] = [];
  fhirResources.forEach((fhirResource: FhirResource) => {
    bundleEntries.push(FHIRUtils.buildResourceEntry(fhirResource, false));
  });

  bundleEntries.push(FHIRUtils.buildResourceEntry(provenancce, false)); //Provenance

  fhirBundle.entry = bundleEntries;

  return fhirBundle;
}

export function buildProvenance(
  targetRef: string, //eg:  Observation/dffdgdg-2343-35554-34r5435
  who: { reference: string; display: string }, //eg. {Practitioner/oa2,johnsmith}
  // Both fields admit `undefined` — see the defect note on `build` above; a member
  // with no managing organization reaches here with both absent, and the
  // `'Organization/' + org.code` below then records `"Organization/undefined"`.
  org: { code: string | undefined; display: string | undefined }, //eg. {Organization/HUMANA,humana}
  subjectRef: string, //eg. Patient/1212
  occuredDate: string,
  recordedDate: string,
  activityCode: string, //CREATE, UPDATE, DELETE
  activityDesc: string,
  reasonCode: string, //LG41764-8: Food Insecurity
  reasonDesc: string //Screening
): Provenance {
  const provenanceId = uuid();
  const provenancePayload: Provenance = {
    resourceType: 'Provenance',
    id: provenanceId,
    target: [
      {
        reference: targetRef,
      },
    ],
    agent: [
      {
        // Updated By(Practitioner Id)
        who: {
          reference: who.reference,
          display: who.display,
        },
      },
      {
        // For Organization Id
        who: {
          reference: 'Organization/' + org.code,
          display: org.display,
        },
      },
    ],
    occurredDateTime: occuredDate,
    recorded: recordedDate,
    activity: {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
          code: activityCode,
          display: activityDesc,
        },
      ],
    },
    reason: [
      {
        coding: [
          {
            system: 'http://snomed.info/sct',
            code: reasonCode,
            display: reasonDesc,
          },
        ],
      },
    ],
  };
  if (targetRef !== subjectRef) {
    provenancePayload.target.push({
      reference: subjectRef,
    });
  }
  return provenancePayload;
}
